import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from '../../PlatformFunctionality';
import { submitTracking } from '../../Tracking/utils/shopifyActionTrackingHelper';
import { TrackingDescriptorType } from '../../Tracking/types';

const LOCAL_STORAGE_KEY = 'compareItems';

interface CompareTrackingData {
  trackingDescriptor?: TrackingDescriptorType;
  gtm?: any;
}

export interface ProductCompareProviderInterface {
  itemsToCompare: Array<{ sku: string; imageUrl: string }>;
  compareSelectedItems: () => void;
  clearAllComparedItems: () => void;
  addItemToCompare: (sku: string, imageUrl: string, trackingData?: CompareTrackingData) => void;
  removeItemFromCompare: (sku: string) => void;
  isCompareLimitReached: boolean;
  setIsCompareLimitReached: Dispatch<SetStateAction<boolean>>;
  compareProductGroupIds: Array<string>;
  shouldHideCompare: boolean;
}

export const ProductCompareContext = createContext<ProductCompareProviderInterface | undefined>(undefined);

const getCurrentlyComparedItems = () => {
  const currentlyCompared = window?.localStorage.getItem(LOCAL_STORAGE_KEY);
  if (currentlyCompared) {
    return new Map<string, string>(JSON.parse(currentlyCompared));
  }
  return new Map<string, string>();
};

interface ProductCompareProviderProps {
  compareProductGroupIds: Array<string>;
  shouldHideCompare?: boolean;
  children: ReactNode;
}

export const ProductCompareProvider = ({
  compareProductGroupIds,
  shouldHideCompare = false,
  children,
}: ProductCompareProviderProps) => {
  const [itemsToCompareMap, setItemsToCompareMap] = useState<Map<string, string>>(getCurrentlyComparedItems());
  const [isCompareLimitReached, setIsCompareLimitReached] = useState(false);
  const navigate = useNavigate();
  const itemsToCompare = useMemo(
    () => [...itemsToCompareMap].map(([sku, imageUrl]) => ({ sku, imageUrl })),
    [itemsToCompareMap]
  );

  const compareSelectedItems = () => {
    const queryString = [...itemsToCompareMap].map(([sku]) => `sku=${sku}`).join('&');
    navigate(`/pages/compare?${queryString}`);
  };

  const clearAllComparedItems = () => {
    setItemsToCompareMap(new Map<string, string>());
  };

  const removeItemFromCompare = (sku: string) => {
    setItemsToCompareMap(prev => new Map([...prev].filter(item => item[0] !== sku)));
  };

  const addItemToCompare = (sku: string, imageUrl: string, trackingData?: CompareTrackingData) => {
    const isCompareFull = itemsToCompareMap.size >= 4;
    if (!isCompareFull) {
      setItemsToCompareMap(prev => new Map([...prev, [sku, imageUrl]]));
    } else {
      setIsCompareLimitReached(true);
    }
    if (trackingData?.trackingDescriptor) {
      submitTracking({
        event: 'productCompare',
        action: 'addCard',
        trackingDescriptor: trackingData.trackingDescriptor,
        gtm: trackingData.gtm,
      });
    }
  };

  // Refresh the items in compare when the tab becomes visible to prevent different tabs having different items in compare
  useEffect(() => {
    const refreshItemsToCompare = () => {
      if (document.visibilityState === 'visible') {
        setItemsToCompareMap(getCurrentlyComparedItems());
      }
    };
    window?.addEventListener('visibilitychange', refreshItemsToCompare);
    return () => {
      window?.removeEventListener('visibilitychange', refreshItemsToCompare);
    };
  }, []);

  useEffect(() => {
    window?.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([...itemsToCompareMap]));
    itemsToCompareMap.size < 4 && setIsCompareLimitReached(false);
  }, [itemsToCompareMap]);

  return (
    <ProductCompareContext.Provider
      value={{
        itemsToCompare,
        compareSelectedItems,
        clearAllComparedItems,
        addItemToCompare,
        removeItemFromCompare,
        isCompareLimitReached,
        setIsCompareLimitReached,
        compareProductGroupIds,
        shouldHideCompare,
      }}
    >
      {children}
    </ProductCompareContext.Provider>
  );
};

export const useProductCompare = (isProviderRequired = true) => {
  const context = useContext(ProductCompareContext);

  if (context == null) {
    if (isProviderRequired) {
      throw new Error('useProductCompare must be used within a useProductCompareContext');
    }
    return {
      shouldShowCompare: () => false,
      compareHandleClick: () => null,
      isAddedToCompare: () => false,
      itemsToCompare: [],
      compareSelectedItems: () => null,
      clearAllComparedItems: () => null,
      addItemToCompare: () => null,
      removeItemFromCompare: () => null,
    };
  }

  const shouldShowCompare = (productGroupId?: number) => {
    if (context.shouldHideCompare || !productGroupId) {
      return false;
    }
    return context.compareProductGroupIds?.includes(productGroupId.toString()) ?? false;
  };

  const isAddedToCompare = (sku: string) => context.itemsToCompare.some(item => item.sku === sku);

  const compareHandleClick = (sku: string, imageUrl: string, trackingData?: CompareTrackingData) => {
    if (isAddedToCompare(sku)) {
      context.removeItemFromCompare(sku);
    } else {
      context.addItemToCompare(sku, imageUrl, trackingData);
    }
  };

  return {
    shouldShowCompare,
    isAddedToCompare,
    compareHandleClick,
    itemsToCompare: context.itemsToCompare,
    compareSelectedItems: context.compareSelectedItems,
    clearAllComparedItems: context.clearAllComparedItems,
    addItemToCompare: context.addItemToCompare,
    removeItemFromCompare: context.removeItemFromCompare,
    isCompareLimitReached: context.isCompareLimitReached,
    setIsCompareLimitReached: context.setIsCompareLimitReached,
  };
};
